/**
 * Price Unit Runtime Guards
 *
 * Validates that price values are in cents (0-100), not fractional (0-1).
 * One wrong caller → 100x exposure miscalculation.
 *
 * @ai-context
 *   Role: Runtime safety net for the cents/fractional boundary.
 *   Pattern: Pure assertion function. Returns input on success, throws on violation.
 *   Purity: No side effects.
 *
 * @ai-invariant Values strictly between 0 and 1 are almost certainly wrong —
 *   Kalshi/Poly minimum tick is 1 cent. Values of exactly 0 or 1 are valid edge prices.
 *
 * @ai-downstream
 *   - orderMath.ts → computePayoutPerContract
 *   - exposureLimits.ts → computeExposure
 */

import { boundaryError } from '../infra/boundaryLog';

/**
 * Assert that a price value is in cents (0-100).
 * Throws if value is out of range or looks fractional (strictly between 0 and 1).
 * Production hotfix from S36: round first to handle float artifacts.
 * Returns rounded value for inline use.
 */
export function assertCents(value: number, label: string): number {
  const rounded = Math.round(value);
  if (rounded < 0 || rounded > 100) {
    boundaryError('price-unit', `${label}: ${value} -> ${rounded} out of range`, {
      value,
      rounded,
    });
    throw new Error(`${label}: priceCents ${value} out of range 0-100`);
  }
  if (value > 0 && value < 1) {
    boundaryError('price-unit', `${label}: ${value} looks fractional`, { value });
    throw new Error(`${label}: priceCents ${value} looks fractional (0-1), expected cents (0-100)`);
  }
  return rounded;
}
